// src/hooks/results/useSpirometryResults.js
import { useEffect, useMemo, useRef, useState } from "react";
import {
  collection,
  limit,
  onSnapshot,
  orderBy,
  query,
  where,
} from "firebase/firestore";
import { documentId } from "firebase/firestore";
import {
  parseTimestampFromRecordingId,
  formatDateParts,
} from "../../utils/results/recordingIdTime";
import {
  getInitialRealtimeListenLimit,
  getNextRealtimeListenLimit,
  isNullishSubjectId,
  selectRealtimeMeasurementDocs,
  shouldExpandRealtimeListenLimit,
} from "./loadStagedMeasurementDocs";
import { pickSpirometryResult } from "../../utils/results/spirometry";

export function useSpirometryResults(
  db,
  uid,
  { pageSize = 50, subjectId = null } = {},
) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(!!db && !!uid);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const [error, setError] = useState(null);
  const hasLoadedSnapshotRef = useRef(false);

  const colRef = useMemo(() => {
    if (!db || !uid) return null;
    return collection(db, "measurements", uid, "RecordingSpirometry");
  }, [db, uid]);

  const activeSubject = useMemo(
    () => (subjectId ? String(subjectId).trim() : ""),
    [subjectId],
  );
  const hasClientFilter = !activeSubject;
  const initialListenLimit = useMemo(
    () => getInitialRealtimeListenLimit(pageSize, hasClientFilter),
    [pageSize, hasClientFilter],
  );
  const [listenLimit, setListenLimit] = useState(initialListenLimit);

  useEffect(() => {
    hasLoadedSnapshotRef.current = false;
    setListenLimit(getInitialRealtimeListenLimit(pageSize, hasClientFilter));
    setLoading(!!colRef);
    setLoadingMore(false);
    setHasMore(false);
    setError(null);
  }, [colRef, activeSubject, hasClientFilter]);

  useEffect(() => {
    if (!colRef) {
      setRows([]);
      setLoading(false);
      setLoadingMore(false);
      setHasMore(false);
      setError(null);
      return;
    }

    let cancelled = false;

    if (!hasLoadedSnapshotRef.current) {
      setLoading(true);
      setLoadingMore(false);
    }
    setError(null);

    const baseConstraints = [];

    if (activeSubject) {
      baseConstraints.push(where("subjectId", "==", activeSubject));
    }

    baseConstraints.push(orderBy(documentId(), "desc"));

    const clientFilter = activeSubject
      ? null
      : (data) => isNullishSubjectId(data?.subjectId);

    const mapDocsToRows = (docs) =>
      docs.map((doc) => {
        const data = doc.data() || {};
        const ts = parseTimestampFromRecordingId(doc.id);
        const { time, date } = formatDateParts(ts, data?.language || "en-GB");
        const { fev1, pef } = pickSpirometryResult(data);

        return {
          id: doc.id,
          timestamp: ts ? ts.toISOString() : null,
          time,
          date,
          fev1,
          pef,
          raw: data,
        };
      });

    const qy = query(colRef, ...baseConstraints, limit(listenLimit));

    const unsub = onSnapshot(
      qy,
      (snap) => {
        if (cancelled) return;

        const docs = selectRealtimeMeasurementDocs(
          snap.docs,
          pageSize,
          clientFilter,
        );

        setRows(mapDocsToRows(docs));
        hasLoadedSnapshotRef.current = true;
        setLoading(false);
        setHasMore(snap.size >= listenLimit);

        const needsExpansion = shouldExpandRealtimeListenLimit(
          snap.size,
          listenLimit,
          docs.length,
          pageSize,
        );

        setLoadingMore(needsExpansion);

        if (needsExpansion) {
          setListenLimit((currentLimit) =>
            getNextRealtimeListenLimit(
              currentLimit,
              pageSize,
              hasClientFilter,
            ),
          );
        }
      },
      (err) => {
        if (cancelled) return;
        setError(err);
        setLoading(false);
        setLoadingMore(false);
        setHasMore(false);
      },
    );

    return () => {
      cancelled = true;
      unsub();
    };
  }, [colRef, pageSize, activeSubject, listenLimit, hasClientFilter]);

  return { rows, loading, loadingMore, hasMore, error };
}
